import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import aiChatbotsService, { ChatbotModule, ModuleCatalogItem } from "../../../../../features/ai_chatbots/aiChatbotsService";

interface Props {
  chatbotId?: number | null;
}

const ModulesTab: React.FC<Props> = ({ chatbotId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<ModuleCatalogItem[]>([]);
  const [modules, setModules] = useState<ChatbotModule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!chatbotId) return;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [c, m] = await Promise.all([
          aiChatbotsService.getModulesCatalog(),
          aiChatbotsService.getChatbotModules(chatbotId),
        ]);
        setCatalog(c || []);
        setModules(m || []);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || "Failed to load chatbot modules");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [chatbotId]);

  const enabledModules = useMemo(() => {
    const enabledKeys = new Set(modules.filter((m) => m.status === "enabled").map((m) => m.module_key));
    return catalog.filter((c) => enabledKeys.has(c.module_key));
  }, [catalog, modules]);

  const moduleTargetMap: Record<string, string> = {
    bms: "/bms/users",
  };

  if (!chatbotId) {
    return <Alert variant="info">{t("bms_modules_after_creation")}</Alert>;
  }

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {error && <Alert variant="danger">{error}</Alert>}
      {!enabledModules.length && <Alert variant="info">{t("bms_no_active_modules")}</Alert>}
      {enabledModules.map((item) => {
        const targetPath = moduleTargetMap[item.module_key];
        return (
          <Card
            key={item.module_key}
            className={targetPath ? "cursor-pointer" : ""}
            onClick={() => targetPath && navigate(targetPath)}
          >
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-bold">{item.name}</div>
                <small className="text-muted">{item.description || item.module_key}</small>
              </div>
              <span className="badge bg-success">{t("bms_configurations")}</span>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
};

export default ModulesTab;

